import { omit, pick } from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Playlist from '../Playlist';
import nullifyEmptyStrings from '../../lib/utils/nullifyEmptyStrings';
import { commitMutation, graphql } from 'react-relay';
import { GlobalNotifications, Intent } from '../Modules';

class UserPlaylist extends Component {
  static propTypes = {
    node: PropTypes.object.isRequired,
    deletePlaylist: PropTypes.func,
    showControls: PropTypes.bool,
    parentID: PropTypes.string,
    relay: PropTypes.object.isRequired,
    goBack: PropTypes.func
  };

  constructor(props) {
    super(props);
    this.createPlaylistSong = this.createPlaylistSong.bind(this);
    this.deletePlaylistSong = this.deletePlaylistSong.bind(this);
    this.updatePlaylist = this.updatePlaylist.bind(this);
    this.playlistContainsSong = this.playlistContainsSong.bind(this);
  }

  playlistContainsSong(song) {
    for (const playlistSong of this.props.node.playlist_songs.edges) {
      if (playlistSong.node.song.isrc === song.isrc) return true;
    }
    return false;
  }

  deletePlaylistSong(e, song, parent, successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to delete song',
        intent: Intent.DANGER
      });
    };

    const onCompleted = () => {
      if (successCb) successCb();
    };

    const mutation = graphql`
      mutation UserPlaylist_deletePlaylistSongMutation(
        $input: DeletePlaylistSongInput!
      ) {
        deletePlaylistSong(input: $input) {
          deletedPlaylistSongId
          playlist {
            thumbnail(width: 240, height: 240) {
              id
              width
              height
            }
          }
        }
      }
    `;

    const environment = this.props.relay.environment;

    const configs = [
      {
        type: 'RANGE_DELETE',
        parentID: this.props.node.id,
        pathToConnection: ['playlist', 'playlist_songs'],
        deletedIDFieldName: 'deletedPlaylistSongId',
        connectionKeys: [
          {
            key: 'UserPlaylistContainer_playlist_songs',
            rangeBehavior: 'append'
          },
          {
            key: 'MusicPlannerContainer_playlist_songs',
            rangeBehavior: 'append'
          },
          {
            key: 'PlannerPlaylistContainer_playlist_songs',
            rangeBehavior: 'append'
          },
          {
            key: 'MpTemplateContainer_playlist_songs',
            rangeBehavior: 'append'
          }
        ]
      }
    ];

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id: parent.id
        }
      },
      configs,
      onCompleted,
      onError
    });
  }

  updatePlaylist(data, successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to update playlist',
        intent: Intent.DANGER
      });
    };

    const onCompleted = () => {
      if (successCb) successCb();
    };

    const mutation = graphql`
      mutation UserPlaylist_updatePlaylistMutation(
        $input: UpdatePlaylistInput!
      ) {
        updatePlaylist(input: $input) {
          playlist {
            title
            description
          }
        }
      }
    `;

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id: this.props.node.id,
          [data.name]: data.value
        }
      },
      onCompleted,
      onError
    });
  }

  createPlaylistSong(e, song, parent, successCb, failureCb) {
    if (Array.isArray(song)) {
      this.createPlaylistSongs(e, song, parent, successCb, failureCb);
      return;
    }
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to add song to playlist',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
    };

    const mutation = graphql`
      mutation UserPlaylist_createPlaylistSongMutation(
        $input: CreatePlaylistSongInput!
      ) {
        createPlaylistSong(input: $input) {
          new_playlist_song_edge {
            node {
              id
              position
              song {
                id
                isrc
                name
                artist
                album
                song_images {
                  edges {
                    node {
                      id
                      url
                      width
                      height
                    }
                  }
                }
              }
            }
          }
          playlist {
            thumbnail(width: 240, height: 240) {
              id
              width
              height
            }
          }
        }
      }
    `;

    const songParams = nullifyEmptyStrings(omit(song, 'id'));
    if (songParams.external_source) {
      songParams.external_source = songParams.external_source.toUpperCase();
    }

    const parentID = this.props.node.id;

    const configs = [
      {
        type: 'RANGE_ADD',
        parentID,
        edgeName: 'new_playlist_song_edge',
        connectionInfo: [
          {
            key: 'UserPlaylistContainer_playlist_songs',
            rangeBehavior: 'append'
          },
          {
            key: 'PlannerPlaylistContainer_playlist_songs',
            rangeBehavior: 'append'
          },
          {
            key: 'MusicPlannerContainer_playlist_songs',
            rangeBehavior: 'append'
          }
        ]
      }
    ];

    const updater = (store) => {
      if (this.props.parentID) {
        // If parent playlist is provided and it has no thumbnail,
        // update with new UserPlaylist thumbnail
        const parentPlaylist = store.get(this.props.parentID);
        const payload = store.getRootField('createPlaylistSong');
        const thumbnailArgs = { width: 240, height: 240 };
        const oldThumbnail = parentPlaylist.getLinkedRecord(
          'thumbnail',
          thumbnailArgs
        );
        if (!oldThumbnail) {
          const newThumbnail = payload.getLinkedRecord('playlist')
            .getLinkedRecord(
              'thumbnail',
              thumbnailArgs
            );
          parentPlaylist.setLinkedRecord(
            newThumbnail,
            'thumbnail',
            thumbnailArgs
          );
        }
      }
    };

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          playlist_id: parentID,
          ...songParams,
          song_images: songParams.song_images
            ? songParams.song_images.edges.map(({ node: image }) =>
                pick(image, 'url', 'width', 'height')
              )
            : []
        }
      },
      configs,
      updater,
      onCompleted,
      onError
    });
  }

  createPlaylistSongs(e, songs, parent, successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to add songs to playlist',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
    };

    const mutation = graphql`
      mutation UserPlaylist_createPlaylistSongsMutation(
        $input: CreatePlaylistSongsInput!
      ) {
        createPlaylistSongs(input: $input) {
          playlist {
            ...UserPlaylistContainer_node
            ...RecommendationContainer_node
          }
        }
      }
    `;

    const newSongs = songs.map(song => {
      const newSong = nullifyEmptyStrings(omit(song, 'id', '__typename'));
      if (newSong.external_source) {
        newSong.external_source = newSong.external_source.toUpperCase();
      }
      newSong.song_images = newSong.song_images
        ? newSong.song_images.edges.map(({ node: image }) =>
            pick(image, 'url', 'width', 'height')
          )
        : [];
      return newSong;
    });

    const parentID = this.props.node.id;

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          playlist_id: parentID,
          songs: newSongs
        }
      },
      onCompleted,
      onError
    });
  }

  render() {
    const {
      node,
      deletePlaylist,
      showControls,
      relay,
      goBack
    } = this.props;

    return (
      <Playlist
        playlist={node}
        relay={relay}
        refetchSearchResults={relay.refetch}
        searchResults={node.song_search.edges.map(
          edge => edge.node
        )}
        editable={node.can_edit}
        fillable={node.can_edit}
        showControls={showControls}
        goBack={goBack}
        removeFromPlaylist={this.deletePlaylistSong}
        updatePlaylist={this.updatePlaylist}
        deletePlaylist={deletePlaylist}
        playlistContainsSong={this.playlistContainsSong}
        addToPlaylist={this.createPlaylistSong}
      />
    );
  }
}

export default UserPlaylist;



// WEBPACK FOOTER //
// ./webpack/components/App/UserPlaylist.js